/**
 * sun-sticky-buttons.js — Web Component <sun-sticky-buttons>
 *
 * LISTE 01 PP11-12 — barre « Ajouter au panier + Apple Pay + date de livraison » flottante sur la
 * page produit essentiel, en mobile.
 *
 * Pourquoi du JS ici alors que le thème est SSR-first
 * ---------------------------------------------------
 * `position: sticky; bottom: 0` retient un élément TANT QU'ON NE L'A PAS ENCORE ATTEINT, puis le
 * relâche dès qu'on arrive à sa position naturelle — l'inverse de la spec pour une barre placée
 * juste après les sélecteurs. Et déplacer la barre en fin de page pour que `sticky` fasse le
 * travail casserait le desktop, où elle doit rester sous les sélecteurs. D'où ce composant.
 *
 * Le JS n'orchestre que des classes et des hauteurs — il ne construit aucun DOM (règle SSR-first).
 * Si le script ne charge pas, la barre reste simplement dans le flux : la page demeure achetable.
 *
 * Règle
 * -----
 * Le vrai bouton garde TOUJOURS sa place sous les sélecteurs. La barre n'est épinglée en bas du
 * viewport qu'une fois cet emplacement DÉPASSÉ (sorti par le haut de l'écran) — jusqu'en bas de
 * page. Tant qu'on ne l'a pas encore atteint, rien ne flotte.
 *
 *   - L'emplacement garde la hauteur qu'avait la barre dans le flux : aucun trou, aucun saut de
 *     page. La barre flottante est plus basse (Apple Pay masqué, cf. sun-product.css) : sa propre
 *     hauteur est réservée en `padding-bottom` du <body>, pour ne pas masquer le bas du footer.
 *   - « Dépassé » = le bas de l'emplacement est au-dessus du viewport : la barre reste dans son
 *     emplacement jusqu'à ce qu'il ait disparu, on ne voit jamais l'emplacement vide.
 *   - Transitions : quand l'emplacement sort par le HAUT, la barre glisse depuis le bas de l'écran
 *     (`.is-entering`) ; quand il revient par le haut, elle redescend (`.is-leaving`) avant de
 *     reprendre sa place.
 *
 * (Décision du 01/10/2026 : la spec initiale PP13 garait la barre juste avant LES TAILLES, soit
 * ~130px de scroll flottant derrière des accordéons fermés — imperceptible.
 * Décision du 07/10/2026 : plus de barre flottante en haut de page, avant d'avoir atteint le
 * bouton ; seulement après l'avoir dépassé, et sans Apple Pay.)
 */
if (!customElements.get('sun-sticky-buttons')) {
  const MOBILE_QUERY = '(max-width: 749px)';
  const BODY_CLASS = 'has-sun-sticky-buttons';
  const HEIGHT_VAR = '--sun-sticky-buttons-height';
  const SLOT_HEIGHT_VAR = '--sun-sticky-buttons-slot-height';

  class SunStickyButtons extends HTMLElement {
    connectedCallback() {
      // La barre englobe le bloc boutons ET les blocs app qui le suivent (date de livraison
      // estimée) : cf. sections/sun-product.liquid.
      this.bar = this.querySelector('.sun-sticky-buttons__bar');
      if (!this.bar) return;

      this.frame = null;
      this.isLeaving = false;
      this.mediaQuery = window.matchMedia(MOBILE_QUERY);

      this.requestUpdate = this.requestUpdate.bind(this);
      this.onAnimationEnd = this.onAnimationEnd.bind(this);

      window.addEventListener('scroll', this.requestUpdate, { passive: true });
      window.addEventListener('resize', this.requestUpdate, { passive: true });
      this.mediaQuery.addEventListener('change', this.requestUpdate);
      this.bar.addEventListener('animationend', this.onAnimationEnd);

      // La hauteur de la barre change quand le libellé du bouton bascule (« Rupture de stock » +
      // bouton restock), quand Apple Pay ou la date de livraison se chargent après coup…
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.requestUpdate);
        this.resizeObserver.observe(this.bar);
      }

      this.requestUpdate();
    }

    disconnectedCallback() {
      if (this.frame) cancelAnimationFrame(this.frame);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.mediaQuery) this.mediaQuery.removeEventListener('change', this.requestUpdate);
      window.removeEventListener('scroll', this.requestUpdate);
      window.removeEventListener('resize', this.requestUpdate);
      if (this.bar) this.bar.removeEventListener('animationend', this.onAnimationEnd);
      this.unpin();
    }

    requestUpdate() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.update();
      });
    }

    update() {
      if (!this.bar) return;

      if (!this.mediaQuery.matches) {
        this.unpin();
        return;
      }

      const isFloating = this.classList.contains('is-floating');

      // L'emplacement garde en flottant la hauteur mesurée dans le flux : sa position est
      // stable, sans oscillation. La barre flottante (sans Apple Pay) est re-mesurée à chaque passe.
      if (!isFloating) this.slotHeight = this.bar.offsetHeight;

      const slot = this.getBoundingClientRect();
      const isSlotPassed = slot.bottom <= 0;

      if (!isSlotPassed) {
        // Retour par le haut : la barre redescend avant de reprendre sa place (cf. en-tête).
        if (isFloating && !this.isLeaving) this.leave();
        return;
      }

      // On repart vers le bas pendant la sortie : la barre remonte depuis sa position actuelle.
      if (this.isLeaving) {
        this.isLeaving = false;
        this.bar.classList.remove('is-leaving');
        this.enter();
      } else if (!isFloating) {
        this.enter();
      }

      this.style.setProperty(SLOT_HEIGHT_VAR, `${this.slotHeight}px`);
      this.classList.add('is-floating');
      document.body.classList.add(BODY_CLASS);
      document.body.style.setProperty(HEIGHT_VAR, `${this.bar.offsetHeight}px`);
    }

    enter() {
      this.bar.classList.remove('is-entering');
      // Reflow forcé : relance l'animation si la classe vient d'être retirée.
      void this.bar.offsetWidth;
      this.bar.classList.add('is-entering');
    }

    leave() {
      this.bar.classList.remove('is-entering');
      this.bar.classList.add('is-leaving');

      // Animation désactivée (prefers-reduced-motion) : `animationend` ne viendra jamais.
      if (getComputedStyle(this.bar).animationName === 'none') {
        this.unpin();
        return;
      }

      this.isLeaving = true;
    }

    onAnimationEnd(event) {
      if (event.target !== this.bar) return;

      this.bar.classList.remove('is-entering');
      if (!this.isLeaving) return;

      this.unpin();
      // L'utilisateur a pu continuer à scroller pendant l'animation : on re-vérifie.
      this.requestUpdate();
    }

    unpin() {
      this.isLeaving = false;
      if (this.bar) this.bar.classList.remove('is-entering', 'is-leaving');
      this.classList.remove('is-floating');
      this.style.removeProperty(SLOT_HEIGHT_VAR);
      document.body.classList.remove(BODY_CLASS);
      document.body.style.removeProperty(HEIGHT_VAR);
    }
  }

  customElements.define('sun-sticky-buttons', SunStickyButtons);
}
